import { useCallback, useMemo, useState } from 'react';

import AlertMessage, {
  AlertMessageType,
} from '@gasbi/shared/common/components/AlertMessage';
import { VirtualizedMultiSelectField } from '@gasbi/shared/common/components/FormFields';
import {
  AutocompleteFieldDynamic,
  CheckboxField,
  InputAlphanumericField,
  InputNumericField,
} from '@gasbi/shared/common/components/FormFields';
import { InputQuantityField } from '@gasbi/shared/common/components/FormFields';
import { EquipmentStatus } from '@gasbi/shared/common/features/gasConsumingEquipment';
import { useConfirmFormCancel } from '@gasbi/shared/common/hooks/useConfirmFormCancel';
import { useFormMode } from '@gasbi/shared/common/hooks/useForm';
import {
  FormikWrapperOnSubmit,
  useFormikWithMutation,
} from '@gasbi/shared/common/hooks/useFormikWithMutation';
import { useIdempotenceKey } from '@gasbi/shared/common/hooks/useIdempotenceKey';
import { RequirePermission } from '@gasbi/shared/common/providers/PermissionProvider/components/RequirePermission';
import { FormFieldPreview } from '@gasbi/shared/index';
import { Row } from '@gasbi/shared/web';
import { FormDialog } from '@gasbi/shared/web/components';
import { AutocompleteSelectField } from '@gasbi/shared/web/components/AutocompleteSelect';
import { DialogContent, DialogType } from '@gasbi/shared/web/components/Dialog';
import DialogTitle from '@gasbi/shared/web/components/Dialog/DialogTitle';
import { buildGridifyQuery } from '@gasbi/shared/web/components/Filter';
import {
  makeFilterItemComplex,
  makeFilterItemPrimitive,
  makeFilterItemTextSearch,
} from '@gasbi/shared/web/components/Filter/buildGridifyQuery/utils';
import { FormFooter } from '@gasbi/shared/web/components/FormFooter';
import { FormSection } from '@gasbi/shared/web/components/FormSection';
import {
  AutocompleteWithHook,
  validationSchemaGasConsumingEquipmentForm as validationSchema,
} from '@gasbi/shared/web/forms/GasConsumingEquipmentForm';
import { mapValues, omit } from 'lodash-es';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import EquipmentIcon from '@/assets/equipment.svg?react';
import { EquipmentStatusChip } from '@/components/EquipmentStatusChip';
import { ServiceOrganizationFieldGroup } from '@/components/FormFields/ServiceOrganizationFieldGroup';
import { useGetEquipmentGroupsIInfiniteQuery } from '@/features/equipmentGroupApi';
import { useGetEquipmentManufacturersInfiniteQuery } from '@/features/equipmentManufacturerApi';
import { useGetEquipmentTypesIInfiniteQuery } from '@/features/equipmentTypeApi';
import {
  useAddGasConsumingEquipmentMutation,
  useGetGasConsumingEquipmentByIdQuery,
  useUpdateGasConsumingEquipmentMutation,
} from '@/features/gasConsumingEquipments';
import { meteringPointsApi } from '@/features/meteringPoints/meteringPointsApi';
import { UserPermissions } from '@/features/permissions';
import { Pages } from '@/pages';

import Divider from '@mui/joy/Divider';
import Stack from '@mui/joy/Stack';

import { useFetchFormData } from './hooks/useFetchFormData';
import useGetInitialValues from './hooks/useGetInitialValues';
import { FormValues } from './types';

type GasConsumingEquipmentFormProps = {
  isModalOpen?: boolean;
  onDialogClose?: () => void;
  type?: DialogType;
  meteringNodeId: string;
};

export const GasConsumingEquipmentForm = ({
  isModalOpen,
  onDialogClose,
  type = DialogType.Create,
  meteringNodeId,
}: GasConsumingEquipmentFormProps) => {
  const dispatch = useDispatch();
  const { gasConsumingEquipmentId, personalAccountId } = useParams<{
    gasConsumingEquipmentId?: string;
    personalAccountId: string;
  }>();

  const { data: gasConsumingEquipment } = useGetGasConsumingEquipmentByIdQuery(
    { meteringPointId: personalAccountId, gasConsumingEquipmentId },
    {
      skip: !gasConsumingEquipmentId || !personalAccountId,
    },
  );

  const { setFormType, isCreateMode, isPreviewMode } = useFormMode({
    type,
  });
  const dialogTitle = isCreateMode ? '' : gasConsumingEquipment?.name;

  const statusLiquidated = useMemo(
    () => gasConsumingEquipment?.status === EquipmentStatus.Liquidated,
    [gasConsumingEquipment?.status],
  );
  const statusRemovedOrInstalled = useMemo(
    () =>
      gasConsumingEquipment?.status === EquipmentStatus.InOperation ||
      gasConsumingEquipment?.status === EquipmentStatus.Disconnected,
    [gasConsumingEquipment?.status],
  );

  useIdempotenceKey();

  const useMutation = useMemo(
    () =>
      isCreateMode
        ? useAddGasConsumingEquipmentMutation
        : useUpdateGasConsumingEquipmentMutation,
    [isCreateMode],
  );

  const navigate = useNavigate();

  const {
    isLoading,
    node,
    meteringNodes,
    meteringNodesIsLoading,
    meteringNodesIsFetching,
    meteringNodesNext,
    meteringNodesHasNextPage,
    meteringNodesSearch,
    setMeteringNodesSearch,
  } = useFetchFormData({
    meteringPointId: personalAccountId,
    meteringNodeId,
  });

  const initialValues = useGetInitialValues({
    personalAccountId,
    gasConsumingEquipment,
    node,
  });

  const onSubmit: FormikWrapperOnSubmit<FormValues> = ({ result }) => {
    dispatch(meteringPointsApi.util.invalidateTags(['GasMeteringNode']));
    if (isCreateMode) {
      onDialogClose();
      navigate(`./${Pages.GAS_CONSUMING_EQUIPMENT}/${result}`);
    } else {
      setFormType(DialogType.Preview);
    }
  };

  const normalizeRequestData = useCallback(
    values => {
      const payload = omit(values, [
        'gasMeteringNodes',
        'equipmentType',
        'equipmentGroup',
        'equipmentManufacturer',
        'serviceOrganization',
      ]);
      payload.meteringPointId = personalAccountId;
      payload.gasMeteringNodeId = values.gasMeteringNodes?.[0]?.id;
      payload.equipmentGroupId = values.equipmentGroup?.id;
      payload.equipmentTypeId = values.equipmentType?.id;
      payload.equipmentManufacturerId = values.equipmentManufacturer?.id;
      payload.serviceOrganizationId = values.serviceOrganization?.id;
      return mapValues(payload, value => (value === '' ? null : value));
    },
    [personalAccountId],
  );

  const formik = useFormikWithMutation<FormValues>({
    initialValues,
    validationSchema,
    options: {
      enableReinitialize: true,
    },
    onSubmit,
    onError: e => console.error('Failed to create GasConsumingEquipments', e),
    useMutation,
    normalizeRequestData,
  });

  const { submitForm, resetForm, dirty, isLoading: isMutationLoading } = formik;

  const handleCancel = useConfirmFormCancel({
    resetForm,
    dirty,
    onClose: onDialogClose,
  });

  const [equipmentManufacturerSearch, setEquipmentManufacturerSearch] =
    useState('');

  const queryParams = useMemo(
    () =>
      buildGridifyQuery([
        makeFilterItemTextSearch('value', equipmentManufacturerSearch),
        makeFilterItemPrimitive('isActive', true),
      ])
        .addOrderBy('value')
        .build(),
    [equipmentManufacturerSearch],
  );

  const { data: equipmentManufacturers = [], ...equipmentManufacturersQuery } =
    useGetEquipmentManufacturersInfiniteQuery(queryParams, {
      skip:
        equipmentManufacturerSearch.length < 1 &&
        !formik.values.equipmentManufacturer,
    });

  const equipmentManufacturersOptions = useMemo(
    () =>
      equipmentManufacturers?.map(el => ({
        id: el.id,
        name: el.value,
      })),
    [equipmentManufacturers],
  );

  return (
    <FormDialog isModalOpen={isModalOpen} onDialogClose={onDialogClose}>
      <DialogTitle
        type={type}
        title={dialogTitle}
        icon={<EquipmentIcon />}
        subtitle='Обладнання'
      />
      <DialogContent dividers>
        <Stack divider={<Divider />} gap={2}>
          <FormSection title='Загальна інформація'>
            {isPreviewMode && (
              <FormFieldPreview label='Статус'>
                <EquipmentStatusChip status={gasConsumingEquipment?.status} />
              </FormFieldPreview>
            )}
            <RequirePermission
              name={UserPermissions.PersonalAccountsMeteringNodeRead}
              fitContent={false}
            >
              <VirtualizedMultiSelectField
                control={formik}
                preview={isPreviewMode}
                name='gasMeteringNodes'
                label='Вузол обліку'
                options={meteringNodes}
                required
                loading={meteringNodesIsFetching || meteringNodesIsLoading}
                hasNextPage={meteringNodesHasNextPage}
                onLoadMore={meteringNodesNext}
                value={formik.values.gasMeteringNodes || []}
                searchText={meteringNodesSearch}
                onSearchTextChange={setMeteringNodesSearch}
                disabled
              />
            </RequirePermission>
            <AutocompleteSelectField
              control={formik}
              name='equipmentGroupId'
              autocompleteValueName='equipmentGroup'
              label='Група'
              disabled={statusRemovedOrInstalled}
              queryHook={useGetEquipmentGroupsIInfiniteQuery}
              preview={isPreviewMode}
              onChange={value => {
                formik.setFieldValue('equipmentGroupId', value?.id);
                formik.setFieldValue('equipmentType', null);
              }}
              queryFilters={[
                makeFilterItemPrimitive('isActive', true),
                makeFilterItemComplex('consumerType', [
                  'CommercialAndPrivate',
                  'Private',
                ]),
              ]}
              required
            />
            <Row>
              <AutocompleteWithHook
                required
                control={formik}
                name='equipmentType'
                onChange={value => formik.setFieldValue('equipmentType', value)}
                preview={isPreviewMode}
                hook={useGetEquipmentTypesIInfiniteQuery}
                label='Марка'
                extraProps={{
                  equipmentGroupId: formik.values.equipmentGroupId,
                }}
                disabled={
                  statusRemovedOrInstalled || !formik.values.equipmentGroupId
                }
                queryFilters={[makeFilterItemPrimitive('isActive', true)]}
                loading={isLoading}
              />

              <AutocompleteFieldDynamic
                control={formik}
                name='equipmentManufacturer'
                label='Виробник'
                options={equipmentManufacturersOptions}
                isOptionsDataLoading={equipmentManufacturersQuery?.isLoading}
                searchText={equipmentManufacturerSearch}
                onSearchTextChange={setEquipmentManufacturerSearch}
                onChange={value => {
                  formik.setFieldValue('equipmentManufacturer', value);
                }}
                preview={isPreviewMode}
                hasNextPage={equipmentManufacturersQuery?.hasNextPage}
                onLoadMore={equipmentManufacturersQuery?.fetchNextPage}
                renderPreview={formik.values.equipmentManufacturer?.name}
                required
                requiredSearch
                disabled={statusRemovedOrInstalled}
              />
            </Row>
            <Row>
              <InputAlphanumericField
                control={formik}
                name='equipmentModel'
                label='Модель'
                placeholder='Введіть модель обладнання'
                preview={isPreviewMode}
                disabled={statusRemovedOrInstalled}
                maxLength={25}
                loading={isLoading}
              />
              <InputAlphanumericField
                control={formik}
                name='factoryNumber'
                label='Заводський номер'
                placeholder='Введіть заводський номер обладнання'
                preview={isPreviewMode}
                required
                disabled={statusRemovedOrInstalled}
                maxLength={15}
                loading={isLoading}
              />
            </Row>
          </FormSection>
          <FormSection title='Характеристики'>
            <Row>
              <InputNumericField
                control={formik}
                name='power'
                label='Потужність'
                placeholder='0'
                preview={isPreviewMode}
                min={0}
                max={99999999.99}
                decimalScale={2}
                unit='кВт'
                required
                loading={isLoading}
              />
              <InputNumericField
                control={formik}
                name='efficiency'
                label='ККД'
                placeholder='0'
                preview={isPreviewMode}
                min={0}
                max={99.99}
                decimalScale={2}
                unit='%'
                required
                loading={isLoading}
              />
            </Row>
            <Row>
              <InputNumericField
                control={formik}
                name='hourlyCost'
                label='Годинна витрата'
                placeholder='0'
                preview={isPreviewMode}
                min={0}
                max={99999999.99}
                decimalScale={2}
                unit='кКал/м³'
                required
                loading={isLoading}
              />
              <InputNumericField
                control={formik}
                name='heatingArea'
                label='Опалювальна площа'
                placeholder='0'
                preview={isPreviewMode}
                min={0}
                max={99999999.9}
                decimalScale={1}
                unit='м²'
                loading={isLoading}
              />
            </Row>
            <InputQuantityField
              control={formik}
              name='amount'
              label='Кількість'
              preview={isPreviewMode}
              min={1}
              max={99999}
              loading={isLoading}
            />
            <ServiceOrganizationFieldGroup
              selectFieldName='serviceOrganizationId'
              control={formik}
              isPreviewMode={isPreviewMode}
              loading={isLoading}
            />
          </FormSection>
          <FormSection title='Додатково'>
            <CheckboxField
              control={formik}
              name='isReserve'
              label='Резерв'
              preview={isPreviewMode}
            />
          </FormSection>
        </Stack>
        {formik.errors?.message && (
          <FormSection title=''>
            <AlertMessage type={AlertMessageType.Error} isSelfClosed>
              {formik.errors?.message}
            </AlertMessage>
          </FormSection>
        )}
      </DialogContent>
      <FormFooter
        isCreateMode={isCreateMode}
        isPreviewMode={isPreviewMode}
        isLoading={isMutationLoading}
        setFormType={setFormType}
        handleCancel={handleCancel}
        submitForm={submitForm}
        isEditButtonDisabled={statusLiquidated}
        editPermission={UserPermissions.PersonalAccountsMeteringNodeManage}
      />
    </FormDialog>
  );
};
